import React, { useCallback, useContext, useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_AS_TYPE, INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VERIFICATION_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsDocumentVerification';
import { isDesktopDevice } from 'Utilities/devices';
import { INCODE_POST_SALE_SLUG, POST_SALE_DOCS_START_PAGE } from 'Utilities/routing';
import { safeLocalStorage } from 'Utilities/storages';

import { FAMILY_KEY } from '../../../../vehicleProfiling/VehicleProfiling.constants';
import { DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY, DOCUMENT_KINDS } from '../../../DocumentCapture.helpers';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext } from '../../../DocumentCaptureContexts';
import { useIncodeVerificationUrl } from '../../hooks/useIncodeVerificationUrl';
import { useOneTimeToken } from '../../hooks/useOneTimeToken';
import LocalTexts from '../../UploadDocuments.texts.json';
import { INCODE_VERIFICATION_SELLER_TYPE, useIncodeVerificationDocuments } from '../IncodeSDKPage/hooks/useIncodeVerificationDocuments';

import { UploadDocumentsVerificationWrapper } from './UploadDocumentsVerificationWrapper';

const LocalT = new MDText(LocalTexts.uploadDocumentsVerification);

export const UploadDocumentsVerificationPostSale = () => {
  const { progress } = useContext(DocumentCaptureConfirmationProgressContext);
  const { data: offerData, id: dpEnquiryId } = useContext(DocumentCaptureOfferContext) || {};
  const keeperName = offerData?.seller?.keeperName ?? null;
  const isFamilyOrFriendKeeper = offerData?.seller?.keeperType === FAMILY_KEY;

  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSafeDesktopDevice, setIsSafeDesktopDevice] = useState(false);

  const showFinanceStep = progress?.documents?.find(
    (doc) => doc.kind === DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER,
  )?.required;

  const { token } = useOneTimeToken();

  const authToken = user?.auth_token;

  const onError = useCallback(() => {
    toast({
      content: LocalT.translate('errorContent'),
      heading: LocalT.translate('errorHeading'),
      id: 'incode-verification-error',
      variant: INFOBOX_VARIANT.ERROR,
    });
  }, []);

  const { loading: isVerificationURlLoading } = useIncodeVerificationUrl({
    authToken,
    dpEnquiryId,
    onError,
  });

  const { data, isLoading: isVerificationDocumentsLoading } = useIncodeVerificationDocuments({
    enquiryId: dpEnquiryId,
    verificationType: INCODE_VERIFICATION_SELLER_TYPE,
  });

  useEffect(() => {
    VERIFICATION_ANALYTICS_EVENTS.LOAD_HANDOFF();
    setIsSafeDesktopDevice(isDesktopDevice());
  }, []);

  useEffect(() => {
    const reviewStatusViewVehicles = safeLocalStorage.getItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY) || [];
    const currentEnquiryId = JSON.stringify(dpEnquiryId);

    if (reviewStatusViewVehicles.includes(currentEnquiryId) || !dpEnquiryId) {
      return;
    }

    reviewStatusViewVehicles.push(currentEnquiryId);
    safeLocalStorage.setItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY, reviewStatusViewVehicles);
  }, [dpEnquiryId]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const renderLabelCopy = () => {
    const completed = data?.completedSteps?.length ?? 0;
    const required = data?.requiredSteps?.length ?? 0;

    const hasIncompleteSteps = completed > 0 && completed !== required;

    return hasIncompleteSteps ? 'finishVerification' : 'startVerification';
  };

  const label = LocalT.translate(renderLabelCopy())as string;

  const renderButton = () => {
    const commonProps = {

      disabled: isVerificationURlLoading || isVerificationDocumentsLoading,
      fullWidth: true,
      label,
      loading: isVerificationURlLoading || isVerificationDocumentsLoading,
    };

    if (isSafeDesktopDevice) {
      return (
        <Button
          {...commonProps}
          as={BUTTON_AS_TYPE.BUTTON}
          onClick={() => {
            setIsModalOpen(true);
            VERIFICATION_ANALYTICS_EVENTS.INCODE_QR_MODAL_OPEN({
              label,
              url: POST_SALE_DOCS_START_PAGE,
            });
          }}

        />
      );
    }

    return (
      <Button
        {...commonProps}
        as={BUTTON_AS_TYPE.ANCHOR}
        href={`/${vehicle.vrm}${INCODE_POST_SALE_SLUG}`}
        onClick={VERIFICATION_ANALYTICS_EVENTS.CONTINUE_VERIFICATION}
      />
    );
  };

  return (
    <UploadDocumentsVerificationWrapper
      closeModal={closeModal}
      isFamilyOrFriendKeeper={isFamilyOrFriendKeeper}
      isModalOpen={isModalOpen}
      keeperName={keeperName}
      renderButton={renderButton}
      showFSLStep={showFinanceStep}
      slug={INCODE_POST_SALE_SLUG}
      token={token}
      vehicle={vehicle}
    />
  );
};
